import { listDevices, renameDevice } from '../api/devices';
import { getFamily, renameGroup } from '../api/family';
import { signOutAllPhones } from '../api/pair';
import { userMessage } from '../api/client';
import { clearToken } from '../auth/session';
import {
  dailyAllowance,
  FAM_NAME_RE,
  formatGroupDisplayId,
} from '../lib/group';
import { el, showToast } from '../ui/dom';
import { openRenameModal } from '../ui/modal';

const MAX_DEVICES = 5;

export async function renderDevices(container: HTMLElement): Promise<void> {
  container.innerHTML = '<p class="list-empty">Loading…</p>';

  try {
    const [devices, fam] = await Promise.all([listDevices(), getFamily()]);
    container.innerHTML = '';

    const deviceCount = Math.max(devices.length, fam.device_count || 0, 1);
    const perDevice = fam.fair_use.per_device ?? 50;
    const used = fam.fair_use.daily_used;
    const total = Math.max(fam.fair_use.daily_limit || 0, dailyAllowance(deviceCount, perDevice));
    const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
    const groupId = fam.group_display_id || formatGroupDisplayId(fam.family_id);
    let groupName = (fam.name && fam.name.trim()) || 'My Group';
    const suspended = Boolean(fam.voice_suspended) || used >= total;

    if (suspended) {
      const banner = el('div', { className: 'paused-banner span-full' });
      banner.innerHTML = `
        <div style="display:flex;align-items:flex-start;gap:10px;">
          <div style="font-size:20px;flex-shrink:0;">📋</div>
          <div style="flex:1;min-width:0;">
            <div style="font-size:13px;font-weight:700;color:#8a4a32;margin-bottom:4px;">Voice paused for today</div>
            <div style="font-size:11.5px;color:var(--dark);line-height:1.5;margin-bottom:8px;">
              This Group has used all of today's voice requests, so voice is paused until midnight.
              Your reminders, Board, timers, and announcements keep working.
            </div>
            <a href="mailto:support@remindo.app?subject=Voice%20paused" style="display:inline-block;padding:6px 12px;background:#c97b5c;color:#fff8ec;text-decoration:none;font-size:11px;font-weight:600;border-radius:6px;">Contact support</a>
          </div>
        </div>`;
      container.append(banner);
    }

    const usageCard = el('div', { className: 'card usage-card span-full' });
    const fillClass = pct >= 100 ? 'usage-fill full' : pct >= 80 ? 'usage-fill warn' : 'usage-fill';
    usageCard.innerHTML = `
      <div class="usage-title">Today's AI usage</div>
      <div class="usage-count">
        <span class="used">${Math.min(used, total)}</span>
        <span class="sep">/</span>
        <span class="total">${total}</span>
        <span class="unit">voice requests</span>
      </div>
      <div class="usage-bar"><div class="${fillClass}" style="width:${pct}%;"></div></div>`;
    container.append(usageCard);

    const infoCard = el('div', { className: 'card' });
    const head = el('div', { className: 'dev-head' });
    const headInfo = el('div', { className: 'dev-head-info' });
    const scope = el('div', { className: 'usage-scope' });
    scope.textContent =
      deviceCount > 1 ? `${groupName} · ${deviceCount} Remindos` : groupName;
    const gid = el('div', { className: 'dev-head-gid' });
    gid.innerHTML = `Group ID · <span class="gid-val">${groupId}</span>`;
    headInfo.append(scope, gid);

    const renameFam = el('button', { className: 'fam-edit', type: 'button' }, 'Rename') as HTMLButtonElement;
    renameFam.onclick = () => {
      openRenameModal(
        'Rename Group',
        groupName,
        (nm) => {
          if (!nm || nm === groupName) return '';
          if (!FAM_NAME_RE.test(nm)) return "Use letters, numbers, spaces, or ' - . (max 20)";
          return '';
        },
        async (nm) => {
          const prev = groupName;
          groupName = nm;
          scope.textContent =
            deviceCount > 1 ? `${groupName} · ${deviceCount} Remindos` : groupName;
          try {
            await renameGroup(nm);
            showToast('Group renamed');
          } catch (e) {
            groupName = prev;
            scope.textContent =
              deviceCount > 1 ? `${groupName} · ${deviceCount} Remindos` : groupName;
            showToast(userMessage(e));
          }
        },
      );
    };

    head.append(headInfo, renameFam);
    infoCard.append(head);

    const list = el('div', { className: 'device-list' });
    if (!devices.length) {
      list.append(el('p', { className: 'list-empty' }, 'No devices yet.'));
    } else {
      for (const d of devices) {
        const row = el('div', { className: 'device-row' });
        const dot = el('span', {
          className: `dev-status-dot${d.online ? ' online' : ''}`,
        });
        const mid = el('div', {});
        mid.style.flex = '1';
        mid.style.minWidth = '0';
        const meta = el('div', { className: 'dev-meta' });
        if (d.online) {
          meta.append(el('span', { className: 'online-text' }, 'Online'));
        } else {
          meta.textContent = 'Offline';
        }
        mid.append(el('div', { className: 'dev-name' }, d.name), meta);
        const ren = el('button', { className: 'dev-rename', type: 'button' }, 'Rename') as HTMLButtonElement;
        ren.onclick = () => {
          openRenameModal(
            'Rename this Remindo',
            d.name,
            (nm) => {
              if (!nm || nm === d.name) return '';
              if (!/^[A-Za-z0-9 ]{2,20}$/.test(nm) || !/[A-Za-z]/.test(nm)) {
                return 'Use 2–20 letters or numbers (must include a letter)';
              }
              return '';
            },
            async (nm) => {
              try {
                await renameDevice(d.device_id, nm);
                d.name = nm;
                mid.querySelector('.dev-name')!.textContent = nm;
                showToast('Renamed');
              } catch (e) {
                showToast(userMessage(e));
              }
            },
          );
        };
        row.append(dot, mid, ren);
        list.append(row);
      }
    }
    infoCard.append(list);

    const addLine = el('div', { className: 'add-another' });
    addLine.textContent =
      devices.length >= MAX_DEVICES
        ? `This Group already has ${MAX_DEVICES} Remindos (max).`
        : 'Add another Remindo from the device Remindos tab.';
    infoCard.append(addLine);
    container.append(infoCard);

    const sessionCard = el('div', { className: 'card session-card' });
    sessionCard.append(
      el('div', { className: 'card-title' }, 'This phone'),
      el(
        'div',
        { className: 'card-note' },
        'Lost a phone? This signs every phone out of this page. To get back in, scan the code on any of your Remindos.',
      ),
    );
    const signOut = el(
      'button',
      { className: 'btn btn-ghost signout-btn', type: 'button' },
      'Sign out all phones',
    ) as HTMLButtonElement;
    signOut.onclick = async () => {
      if (
        !confirm(
          'Sign out every phone from this page?\n\nThis one too. To get back in, scan the code on any of your Remindos — takes 30 seconds.',
        )
      ) {
        return;
      }
      try {
        await signOutAllPhones();
        clearToken();
        showToast('All phones signed out');
        window.location.assign('/connect');
      } catch (e) {
        showToast(userMessage(e));
      }
    };
    sessionCard.append(signOut);
    container.append(sessionCard);
  } catch (e) {
    container.innerHTML = '';
    container.append(el('p', { className: 'list-empty' }, userMessage(e)));
  }
}
